import React, { useState } from "react";
import { Drawer, Svg, diagramPadding } from "@common/components/Rendering";
import {
  ArrowId,
  ColumnMarkers,
  coordinate_axis_style,
  coordinate_axis_text_style,
  viewport_icon_size,
} from "./../ViewportStyles";
import { useProject } from "../../features/projects/projectSlice";
import { ng } from "@common/ngds";
import {
  DiagramResults,
  DiagramType,
  DiagramValueRanges,
  DrawAllGraphs,
  drawDiagramExtentAnchors,
  secondOrderDiagramValueRanges,
} from "./drawSectionForceDiagrams";
import { drawLoads } from "./drawLoads";
import { draw_buckling_data } from "./drawBucklingData";
import { drawSupport } from "./drawSupport";
import { getSupports } from "../../helpers/supports";
import { SupportAxis } from "@common/helpers/sharedTypes";
import { drawVerticalMeassurment } from "./drawMeassurement";
import { calculate_column_side_geom_size, drawGeometry } from "./drawSideGeometry";
import {
  selectShowSectionBuckling,
  selectShowSectionDimensions,
  selectShowSectionReactions,
} from "../../features/ui/uiSlice";
import { useSelector } from "react-redux";
import type { GraphPointsMode } from "../Viewport";
import { drawCoordinateSystem } from "./drawCoordinateSystem";
import { useDevConfig } from "../../App";
import { useAutoZoomViewport } from "../../features/user/userSlice";
import { findSectionInProject, geomStyle_from_materialCategory } from "./shared";
import { collectReactionForces, drawReactionForces, ReactionForce } from "./drawReactionForces";
import { display_default_meassure_from_meter } from "@common/helpers/formatting";

export type GraphDirection = "xz" | "xy";

interface ISectionForceDiagramsProps {
  viewDirection: GraphDirection;
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
}

const allLcombGuids = (results: DiagramResults): string[] => {
  const el = Object.keys(results.elementForces);
  const dis = Object.keys(results.displacements);
  const unique = [...new Set([...el, ...dis])];
  return unique;
};

export const SideView = (props: ISectionForceDiagramsProps) => {
  const isDev = useDevConfig();
  const autoZoom = useAutoZoomViewport();
  const [showMoment, setShowMoment] = useState<boolean>(true);
  const [showShear, setShowShear] = useState<boolean>(true);
  const [showAxial, setShowAxial] = useState<boolean>(true);
  const [showDeformations, setShowDeformations] = useState<boolean>(true);

  const { project: projectSource } = useProject();
  const showBuckling = useSelector(selectShowSectionBuckling);
  const showMeassure = useSelector(selectShowSectionDimensions);
  const showReactionForce = useSelector(selectShowSectionReactions);

  const graphs = React.useMemo(() => {
    const project = { ...projectSource, data: ng.frontend.column.Project.fromObject(projectSource?.data) };

    const isAuto = (project?.data as any)?.settings?.endpointCache?.autoBucklingLength ?? true;

    const g = new Drawer<ArrowId>();
    const fixedAxisLength = 0.18;
    const fixedLeftMargin = 0.9;
    const lowerOffsetFactor = 1.1;
    const diagramTypes: DiagramType[] = ["M", "V", "N", "D"];
    const allGuids = allLcombGuids(props.results);

    // Sizing pass: collect one value range per diagram type over EVERY load combination and both
    // view directions. A null drawer measures without drawing, and it runs the same graph-value
    // generation as rendering, so the ranges come from exactly the values that get displayed.
    // Covering all combinations (not just the selected one) means a diagram keeps the same scale
    // when the user switches combination or view direction, and it keeps the pre-drawn hidden
    // combinations inside their lanes - a combination with N ~ 0 can no longer produce a near-zero
    // divisor that throws the other combinations far outside the auto-fit bounding box.
    const diagramValueRanges: DiagramValueRanges = {};
    for (const lcombGuid of allGuids) {
      for (const state of ["xy", "xz"] as GraphDirection[]) {
        const ranges = DrawAllGraphs(null, {
          showMoment,
          showShear,
          showAxial,
          showDeformations,
          graphPointsMode: props.graphPointsMode,
          results: props.results,
          state,
          selectedLcombGuid: lcombGuid,
          visible: false,
          horOffset: 0,
        });
        for (const type of diagramTypes) {
          diagramValueRanges[type] = Math.max(diagramValueRanges[type] ?? 0, ranges[type] ?? 0);
        }
      }
    }

    // props.results only ever holds the order currently on screen - App rebuilds the dictionaries
    // when the 1st/2nd toggle flips - so the loop above measures whichever one is being displayed.
    // Widening it with the 2nd order ranges read straight off the output ties both orders to the
    // same scale. Without it, a 2nd order run can *shrink* the diagram it is being compared with:
    // if another load combination gained more from the 2nd order effects than the one on screen,
    // it raises the shared maximum and everything else is drawn smaller despite larger values.
    // Empty when the output holds no 2nd order results, which leaves the behaviour above unchanged.
    const secondOrderRanges = secondOrderDiagramValueRanges(project.data?.data?.output);
    for (const type of diagramTypes) {
      diagramValueRanges[type] = Math.max(diagramValueRanges[type] ?? 0, secondOrderRanges[type] ?? 0);
    }

    for (const currDir of ["xy", "xz"] as GraphDirection[]) {
      const viewDir = props.viewDirection;
      const is_visible = viewDir == currDir;

      const reactionForces = new Array<ReactionForce>();

      const { height: column_height, size: column_size } = calculate_column_side_geom_size(project.data, currDir);

      const center_line_hor = 0; //-diagramPadding + column_size / 2;
      const graph_offset = diagramPadding;

      // Pin the horizontal extent of the diagram lanes so the auto-fit camera - and with it the
      // column and the three zero axes - does not move when the curves inside them get smaller,
      // which is what switching from the 2nd order results to the 1st order ones does now that
      // both share one scale. Only when there is something to draw diagrams from.
      if (allGuids.length > 0) {
        drawDiagramExtentAnchors(g, {
          showMoment,
          showShear,
          showAxial,
          showDeformations,
          horOffset: graph_offset,
          columnHeight: column_height,
        });
      }

      drawGeometry(g, {
        center_line_hor,
        column_size,
        column_height,
        is_visible,
        geom_style: geomStyle_from_materialCategory(findSectionInProject(project.data)?.sec?.materialCategory),
      });
      drawSupport(g, {
        graphDirection: currDir,
        center_line_hor,
        column_height,
        column_size,
        project: project.data,
        is_visible,
        results: props.results,
        selectedLcombGuid: props.selectedLcombGuid,
      });
      collectReactionForces({
        graphDirection: currDir,
        center_line_hor,
        project: project.data,
        is_visible: is_visible && showReactionForce,
        results: props.results,
        selectedLcombGuid: props.selectedLcombGuid,
        reactionForces,
      });
      const bucklingAxis: SupportAxis = currDir === "xz" ? "Z" : "Y";
      const bucklingSupports = getSupports(project.data, bucklingAxis);
      const isBottomFree = bucklingSupports.some((s) => s.isBaseSupport && s.type === "rigid_roller");
      draw_buckling_data(
        g,
        {
          center_line_hor,
          column_height,
          mode: currDir == "xz" ? props.results.bucklingMode[0] : props.results.bucklingMode[1],
          is_visible: is_visible && showBuckling,
          isBottomFree,
        },
        isAuto,
        currDir == "xz" ? props.results.betas[0] : props.results.betas[1],
      );

      if (
        is_visible &&
        column_size > 0 &&
        Number.isFinite(column_size) &&
        column_height > 0 &&
        Number.isFinite(column_height)
      ) {
        const axisLength = fixedAxisLength;
        const leftMargin = fixedLeftMargin;
        const bottomMargin = axisLength * (showMeassure ? 2.0 : 1.4) * lowerOffsetFactor;
        drawCoordinateSystem(g, {
          origin: [center_line_hor - column_size / 2 - leftMargin, bottomMargin + 0.2],
          axisLength,
          horizontalAxisLabel: currDir === "xy" ? "y" : "z",
          verticalAxisLabel: "x",
          axisStyle: coordinate_axis_style,
          labelStyle: coordinate_axis_text_style,
        });
      }

      // vertical meassurement line for the column
      if (showMeassure) {
        const meassure_dist = 0.4;
        const alignment_dist = 0.05;
        const start_hor = center_line_hor + column_size / 2 + viewport_icon_size.size;
        drawVerticalMeassurment(g, {
          start_hor,
          end_hor: start_hor + meassure_dist,
          alignment: alignment_dist,
          start_vert: 0,
          end_vert: -column_height,
          text: display_default_meassure_from_meter(column_height),
          is_visible,
        });
      }

      // ============= loads
      drawLoads(g, {
        data: project.data,
        viewDirection: currDir,
        column_size,
        center_line_hor,
        display: is_visible,
        selectedLcombGuid: props.selectedLcombGuid,
        showMeassure,
        column_height,
      });

      // Pre-draw non-selected load combinations as hidden drawables so visibility can switch without rebuilding geometry.
      for (const hiddenLcombGuid of allGuids) {
        if (hiddenLcombGuid === props.selectedLcombGuid) continue;
        DrawAllGraphs(g, {
          showMoment,
          showShear,
          showAxial,
          showDeformations,
          graphPointsMode: props.graphPointsMode,
          results: props.results,
          state: currDir,
          selectedLcombGuid: hiddenLcombGuid,
          visible: false,
          horOffset: graph_offset,
          sharedValueRanges: diagramValueRanges,
        });
      }

      DrawAllGraphs(g, {
        showMoment,
        showShear,
        showAxial,
        showDeformations,
        // Forward App button-group mode (off/max/all) to control graph point circles in the diagram renderer.
        graphPointsMode: props.graphPointsMode,
        results: props.results,
        state: currDir,
        selectedLcombGuid: props.selectedLcombGuid,
        visible: is_visible,
        horOffset: graph_offset,
        sharedValueRanges: diagramValueRanges,
      });

      drawReactionForces(g, reactionForces);
    }

    const ret = g.toDrawables();
    return ret;
  }, [
    props.results.elementForces,
    props.results.displacements,
    props.results.supportForces,
    props.viewDirection,
    props.selectedLcombGuid,
    props.graphPointsMode,
    showMoment,
    showShear,
    showAxial,
    showDeformations,
    showBuckling,
    projectSource,
    showMeassure,
    showReactionForce,
    showBuckling,
  ]);

  return <Svg<ArrowId> items={graphs} markers={ColumnMarkers()} isDev={isDev} autoZoom={autoZoom} />;
};
